// Six shortcut cards at the top of the customer dashboard: a tinted card per
// destination with a round coloured icon, title, one-line description, a
// chevron, and a faint oversized copy of the icon as background art.
import React from 'react';
import {Box, ButtonBase, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import WebhookIcon from '@mui/icons-material/Webhook';
import {useHistory} from 'react-router-dom';

import {routeCodes} from '../App';

const ACTIONS = [
  {
    title: 'Send SMS Campaign', body: 'Create and send bulk SMS campaigns to your contacts',
    color: '#2F6FED', Icon: SendIcon, route: () => routeCodes.SMS
  },
  {
    title: 'SMS API Integration', body: 'Integrate SMS API into your applications',
    color: '#F28C28', Icon: WebhookIcon, route: () => routeCodes.DEVELOPERS_TOOL
  },
  {
    title: 'Download Reports', body: 'View detailed reports and analytics',
    color: '#2E9E4F', Icon: BarChartIcon, route: () => routeCodes.SMS_HISTORY
  },
  {
    title: 'Manage Templates', body: 'Create and manage your SMS templates',
    color: '#7C3AED', Icon: ArticleIcon, route: () => routeCodes.TEMPLATE
  },
  {
    title: 'Manage Header / Sender ID', body: 'Add and manage your Sender IDs / Headers',
    color: '#E5484D', Icon: PersonIcon, route: () => `${routeCodes.TEMPLATE}?tab=headers`
  },
  {
    title: 'Manage Profile', body: 'Update your account settings and preferences',
    color: '#1A9AA8', Icon: SettingsIcon, route: () => routeCodes.MY_ACCOUNT
  }
];

const ActionCard = ({title, body, color, Icon, onClick}) => (
  <ButtonBase
    onClick={ onClick }
    sx={{
      position: 'relative', overflow: 'hidden', width: '100%', height: '100%', justifyContent: 'flex-start',
      display: 'flex', alignItems: 'center', gap: 2, p: 2.5, borderRadius: 2, textAlign: 'left',
      bgcolor: alpha(color, 0.07), border: 1, borderColor: alpha(color, 0.12),
      transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
      // Hover: the border takes the card colour and the chevron fills with it.
      '&:hover, &.Mui-focusVisible': {
        borderColor: color, boxShadow: `0 4px 14px ${ alpha(color, 0.22) }`, transform: 'translateY(-1px)',
        '& .quick-action-chevron': {bgcolor: color, color: 'common.white', transform: 'translateX(2px)'}
      }
    }}
  >
    <Icon aria-hidden sx={{position: 'absolute', right: 8, bottom: -10, fontSize: 96, color: alpha(color, 0.07)}} />
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      width: 56, height: 56, borderRadius: '50%', bgcolor: color, color: 'common.white'
    }}>
      <Icon sx={{fontSize: 28}} />
    </Box>
    <Box sx={{flex: 1, minWidth: 0, position: 'relative'}}>
      <Typography sx={{fontSize: 16, fontWeight: 600, color: 'text.primary'}}>{title}</Typography>
      <Typography color="text.secondary" sx={{fontSize: 13, mt: 0.5}}>{body}</Typography>
    </Box>
    <Box className="quick-action-chevron" sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative',
      width: 32, height: 32, borderRadius: '50%', bgcolor: 'common.white', color, boxShadow: 1,
      transition: 'background-color 0.2s, color 0.2s, transform 0.2s'
    }}>
      <ChevronRightIcon fontSize="small" />
    </Box>
  </ButtonBase>
);

const QuickActions = () => {
  const history = useHistory();
  return (
    <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'}}}>
      {ACTIONS.map(({route, ...action}) =>
        <ActionCard key={ action.title } { ...action } onClick={ () => history.push(route()) } />)}
    </Box>
  );
};

export default QuickActions;
